// src/ExpertTestimonials.jsx

import React, { useState, useEffect, useContext } from 'react';
import { Button } from '../components/ui/button';
import { Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { AuthContext } from '../lib/AuthContext';

const ExpertTestimonials = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { accessToken } = useContext(AuthContext); // Get token from AuthContext

  useEffect(() => {
    const fetchTestimonials = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('https://quirkleai.com/api/testimonials/', {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setTestimonials(data);
      } catch (e) {
        console.error("Failed to fetch testimonials:", e);
        setError("Failed to load testimonials. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) { // Only fetch if accessToken is available
      fetchTestimonials();
    }
  }, [accessToken]);

  const filteredTestimonials = testimonials.filter(t => {
    if (activeTab === "All") return true;
    if (activeTab === "High" && t.rating >= 4) return true;
    if (activeTab === "Low" && t.rating < 4) return true;
    return false;
  });

  const allCount = testimonials.length;
  const highRatingCount = testimonials.filter(t => t.rating >= 4).length;
  const lowRatingCount = testimonials.filter(t => t.rating < 4).length;

  const ratingTabs = [
    `All (${allCount})`,
    `High rating (${highRatingCount})`,
    `Low rating (${lowRatingCount})`,
  ];

  const renderStars = (rating) => {
    return (
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-5 w-5 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-10">Loading testimonials...</div>;
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Testimonials and Ratings</h1>
        <div className="flex gap-2">
          <Button variant="outline">📤 Share</Button>
          <Button variant="outline">⚙ Preferences</Button>
        </div>
      </div>

      {/* Rating Tabs */}
      <div className="flex flex-wrap gap-3">
        {ratingTabs.map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab.split(' ')[0] ? 'default' : 'outline'}
            onClick={() => setActiveTab(tab.split(' ')[0])}
          >
            {tab}
          </Button>
        ))}
      </div>

      {/* Testimonials List */}
      {filteredTestimonials.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredTestimonials.map((testimonial) => (
            <Card key={testimonial.id} className="bg-white shadow-sm border">
              <CardContent className="py-4">
                <div className="flex items-center mb-2">
                  {renderStars(testimonial.rating)}
                  <span className="ml-2 text-sm text-gray-600">{testimonial.rating}/5</span>
                </div>
                <p className="text-gray-700 mb-3">"{testimonial.content}"</p>
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span>- {testimonial.client.username}</span>
                  <span>{new Date(testimonial.created_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 text-center py-10 px-6 rounded-md border border-dashed border-gray-300">
          <img
            src="/static/testimonials-empty.png"
            alt="Empty"
            className="mx-auto mb-4"
            style={{ maxWidth: '140px' }}
          />
          <h2 className="text-lg font-semibold text-gray-700">No testimonials found for this filter.</h2>
          <p className="text-sm text-gray-500">Try adjusting your filter or check back later.</p>
        </div>
      )}
    </div>
  );
};

export default ExpertTestimonials;
